import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from '@saleshandy/icons';
import { ImageIcon } from '@saleshandy/design-system';

import Modal from '../../design-system/components/atoms/modal';
import Button from '../../design-system/components/atoms/button';
import Icon from '../../design-system/components/atoms/icon/icon';
import { Images } from '../../app-constants';

import type { LockedFeatureUpsellDefinition } from '../../feature-access/locked-feature-upsell-registry';

const HERO_IMAGES = {
  sentEmailLarge: Images.SentEmailLarge,
} as const;

export interface LockedFeatureUpsellModalProps {
  show: boolean;
  definition: LockedFeatureUpsellDefinition | null;
  onClose: () => void;
  /** Opens `/billings/upgrade` (see provider). */
  onPrimaryAction: () => void;
  /** Plan-based upgrade CTA label (Free vs Starter). */
  primaryLabel: string;
}

/**
 * Presentation-only upsell. Copy is chosen by plan tier in the provider via
 * {@link getLockedFeatureUpsellCopyForPlan}.
 */
export const LockedFeatureUpsellModal: React.FC<LockedFeatureUpsellModalProps> = ({
  show,
  definition,
  onClose,
  onPrimaryAction,
  primaryLabel,
}) => {
  const { t } = useTranslation();

  if (!definition) {
    return null;
  }

  const heroSrc =
    definition.heroPreset && definition.heroPreset in HERO_IMAGES
      ? HERO_IMAGES[definition.heroPreset]
      : HERO_IMAGES.sentEmailLarge;

  return (
    <Modal
      show={show}
      onClose={onClose}
      onHide={onClose}
      backdrop="static"
      className="locked-feature-upsell-modal"
      hideHeader
      hideFooter
      hideSubmitButton
      hideCancelButton
    >
      <div className="locked-feature-upsell-modal__content">
        <button
          type="button"
          className="locked-feature-upsell-modal__close"
          onClick={onClose}
          aria-label={t('labels.close')}
        >
          <Icon identifier="close" />
        </button>

        <header className="locked-feature-upsell-modal__intro">
          <div className="locked-feature-upsell-modal__hero" aria-hidden>
            <div className="locked-feature-upsell-modal__hero-ring">
              <ImageIcon
                src={heroSrc}
                alt=""
                className="locked-feature-upsell-modal__hero-img"
              />
            </div>
          </div>

          <h2 className="locked-feature-upsell-modal__title">
            {definition.title}
          </h2>
          <p className="locked-feature-upsell-modal__subtitle">
            {definition.subtitle}
          </p>
        </header>

        <div
          className="locked-feature-upsell-modal__divider"
          role="presentation"
        />

        <ul className="locked-feature-upsell-modal__features">
          {definition.bulletLines.map((line) => (
            <li key={line} className="locked-feature-upsell-modal__feature">
              <span className="locked-feature-upsell-modal__check-wrap">
                <Check className="locked-feature-upsell-modal__check" />
              </span>
              <span className="locked-feature-upsell-modal__feature-text">
                {line}
              </span>
            </li>
          ))}
        </ul>

        <footer className="locked-feature-upsell-modal__footer">
          <Button
            variant={Button.Variant.Primary}
            size={Button.Size.Large}
            className="locked-feature-upsell-modal__cta"
            onClick={onPrimaryAction}
          >
            {primaryLabel}
          </Button>

          <button
            type="button"
            className="locked-feature-upsell-modal__dismiss"
            onClick={onClose}
          >
            Maybe later
          </button>
        </footer>
      </div>
    </Modal>
  );
};
